import { Button, Divider, Group, Stack } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { ReactNode } from "react";
import { ipawsAlertFormStateSchemaToMessageDataInput, useIpawsAlertFormContext } from "shared/components/ipaws/alert/context";
import { useIpawsAlertFormAllowedEventCodes, useIpawsAlertFormAllowedGeoCodes } from "shared/components/ipaws/alert/query";
import { useIpawsAlertFormActions } from "shared/components/ipaws/alert/useIpawsAlertFormActions";
import { useCustomer } from "shared/graphql/cust";
import { useDirtyFormBlocker } from "shared/hooks/useDirtyFormBlocker";
import { useFormState } from "shared/hooks/useFormState";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { queryClient } from "src/queryClient";

export const IpawsAlertFormStepLayout = (props: { children: ReactNode }) => {
    const form = useIpawsAlertFormContext();
    const actions = useIpawsAlertFormActions();

    const { data: customer } = useCustomer();
    const { isFetching: isAllowedGeoCodesFetching } = useIpawsAlertFormAllowedGeoCodes();
    const { isFetching: isAllowedEventCodesFetching } = useIpawsAlertFormAllowedEventCodes();

    const [step] = useFormState(form, "meta.step");
    const [mode] = useFormState(form, "meta.mode");
    const [channels] = useFormState(form, "channels");

    const nav = useNavigate();
    const blocker = useDirtyFormBlocker(form);

    const isNew = mode == "new";
    const isEdit = typeof mode == "object" && mode.type == "edit";
    const isUpdate = typeof mode == "object" && mode.type == "update";

    const isConfigLoading = step == 0 && channels.length > 0 && (isAllowedGeoCodesFetching || isAllowedEventCodesFetching);

    return (
        <Stack>
            {props.children}
            <Divider />
            <Group>
                {step > 0 ? (
                    <Button variant="default" leftSection={<IconChevronLeft size={16} />} onClick={actions.prevStep}>
                        Back
                    </Button>
                ) : (
                    <div />
                )}
                {step < 3 ? (
                    <Button
                        variant="default"
                        rightSection={<IconChevronRight size={16} />}
                        onClick={actions.nextStep}
                        loading={isConfigLoading}
                    >
                        Next
                    </Button>
                ) : (
                    <Group>
                        {!isUpdate && (
                            <Button
                                variant="transparent"
                                onClick={() =>
                                    blocker.bypass(async () => {
                                        if (form.validate().hasErrors) {
                                            logger.warn("Validation failed with: ", form.errors);
                                            notify.show.warn({ message: "Please fix issues before continuing" });
                                            return;
                                        }

                                        const msg = ipawsAlertFormStateSchemaToMessageDataInput.parse({
                                            ...form.getValues(),
                                            customerId: customer.id,
                                        });

                                        const { close } = await notify.confirm("Are you sure you want to save this draft?");

                                        if (isEdit) {
                                            await actions.updateDraft.mutateAsync({
                                                id: mode.draftId,
                                                msg,
                                            });
                                            await queryClient.invalidateQueries({ queryKey: ["ipaws", "draft", mode.draftId] });
                                        } else {
                                            await actions.createDraft.mutateAsync({
                                                msg,
                                            });
                                        }

                                        await queryClient.invalidateQueries({ queryKey: ["ipaws", "drafts"] });
                                        await nav({ to: "/ipaws/drafts" });

                                        close();
                                    })
                                }
                            >
                                {isNew ? "Save as Draft" : "Save Draft"}
                            </Button>
                        )}
                        <Button
                            onClick={() =>
                                actions.openBroadcastModal({
                                    onConfirm: ({ id }) =>
                                        blocker.bypass(async () => {
                                            await nav({ to: "/ipaws/alerts/$alertId", params: { alertId: id } });
                                        }),
                                })
                            }
                        >
                            Review & Send
                        </Button>
                    </Group>
                )}
            </Group>
        </Stack>
    );
};
